import React, { useState, useCallback, useEffect } from 'react';
import { format } from 'date-fns';
import TableComponent from '../../../components/Table/TableComponent';

import SearchIcon from '@mui/icons-material/Search';
import { TableRow, TableCell } from '@mui/material';
import { useAuth } from '../../../context/AuthContext';
import { fetchMedicalHistory } from '../../../services/SubjectiveServices/MedicalHistoryformService';
import DateRangePicker from '../../../components/FormFilelds/DateRangePicker';

import TablePaginationComponent from '../../../components/TablePaginationComponent';
import { PatientsHistory } from '../../../components/interface/SubjectiveInterfaces/MedicalHistroyInterface';
import IconButton, { ClearButton } from '../../../components/Buttons/SubjectiveIcons';
import SnackbarMessage from '../../../components/SnackBAr/SnackbarNotification';
import LoadingAnimation from '../../../components/LoadingAnimation';


interface MedicalHistoryFormProps {
    patientId: string;
}
const MedicalHistoryForm: React.FC<MedicalHistoryFormProps> = ({ patientId }) => {
    const { token } = useAuth();
    const [medicalhistories, setmedicalHistories] = useState<PatientsHistory[]>([]);
    const [totalMedicalhistories, setTotalMedicalhistories] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
   
    const [dateRange, setDateRange] = useState<{ start: string; end: string }>({ start: '', end: '' });
    const [isButtonEnabled, setIsButtonEnabled] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState('')
    const [snackbarSeverity, setSnackbarSeverity] = useState<
        'success' | 'info' | 'warning' | 'error'
    >('success')
    // Clear filter button visibility
    const [showClearFilterButton, setShowClearFilterButton] = useState(false);
    const showSnackbar = (message: string, severity: 'success' | 'info' | 'warning' | 'error') => {
      setSnackbarMessage(message);
      setSnackbarSeverity(severity);
      setSnackbarOpen(true);
  };

  const fetchMedicalHistoryData = useCallback(async (initial = false) => {
    setLoading(true);
    setError('');

    try {
        const params: Record<string, any> = {
            limit: rowsPerPage,
            page: page + 1,
            order_by: 'values->onset_date',
            dir: 2,
            user_id: patientId,
            from: !initial ? dateRange.start : undefined,
            to: !initial ? dateRange.end : undefined,
            slug: 'medical-history',
            searchkey: searchTerm,
        };

        const data = await fetchMedicalHistory(token!, params);

        const fetchedMedicalhistories: PatientsHistory[] = data.data.patient_histories.map(item => ({
            id: item.id,
            patient_id: Number(patientId),
            consult_id: item.consult_id || null,
            slug: 'medical-history',
            values: {
                condition: {
                    id: item.values.condition.id,
                    icd: item.values.condition.icd ? {
                        code: item.values.condition.icd.code,
                        description: item.values.condition.icd.description
                    } : undefined,
                    name: item.values.condition.name || 'N/A',
                    is_active: item.values.is_active,
                    attributes: item.values.condition.attributes,
                    chronic_illness: item.values.condition.chronic_illness,
                    master_type_slug: item.values.condition.master_type_slug,
                    previous_illness: item.values.condition.previous_illness,
                    notes: item.values.condition.notes
                },
                is_active: item.values.is_active,
                onset_date: item.values.onset_date || 'N/A',
                treated_by: item.values.treated_by || ''
            },
            freeze: item.freeze
        }));

        setmedicalHistories(fetchedMedicalhistories);
        setTotalMedicalhistories(data.data.pagination.total || 0);

        setShowClearFilterButton(
            !initial || searchTerm.trim() !== '' || (dateRange.start !== '' && dateRange.end !== '')
        );

    } catch (error) {
        setError('Failed to fetch Medical histories.');
        console.error('Error fetching Medical histories', error);
        showSnackbar('Failed to fetch Medical histories.', 'error');
    } finally {
        setLoading(false); // Ensure this runs regardless of success or failure
    }
}, [searchTerm, dateRange, page, rowsPerPage, patientId, token]);




    useEffect(() => {
        fetchMedicalHistoryData(true);
    }, [fetchMedicalHistoryData]);

    useEffect(() => {
        const isSearchable = searchTerm.trim() !== '' || (dateRange.start !== '' && dateRange.end !== '');
        setIsButtonEnabled(isSearchable);
    }, [searchTerm, dateRange]);

    const handleSearch = useCallback(() => {
        if (dateRange.start && dateRange.end) {
            setPage(0);
            setLoading(true); // Show spinner while searching
            fetchMedicalHistoryData().finally(() => setLoading(false)); // Hide spinner after search completes
        } else {
            console.warn('Please select both from date and to date.');
            showSnackbar('Please select both from date and to date.', 'warning');
        }
    }, [fetchMedicalHistoryData, dateRange]);
    
    const handleButtonClick = () => {
        handleSearch();

    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setDateRange({ start: '', end: '' });
        setPage(0);
        fetchMedicalHistoryData(true); // Refresh allergies list with no filters
    };

    const formatDate = (dateString: string | number | Date) => {
      try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) {
              return 'Invalid Date'; // Or return an empty string, "N/A", etc.
          }
          return format(date, 'dd-MM-yyyy');
      } catch (error) {
          console.error("Error formatting date:", error);
          return 'Invalid Date'; // Handle invalid date values
      }
  };
  
    
    const renderRow = (item: PatientsHistory, index: number) => (
        <TableRow key={item.id || index}>
            <TableCell className="px-4 py-3 font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'  }}> {page * rowsPerPage + index + 1}</TableCell>
            <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'  }}>{formatDate(item.values.onset_date)}</TableCell>
            <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'  }}>{item.values.condition.name}</TableCell>
            <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'  }}>{item.values.treated_by}</TableCell>
            <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'  }}>  {item.values.is_active === 1 ? 'Active' : 'Inactive'}</TableCell>
        </TableRow>
    );

    return (
        <div>
            <div className="flex flex-wrap items-center gap-4 w-full pb-4">
                <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
                    <DateRangePicker
                        startDateId="allergy_Start_Date"
                        endDateId="allergy_End_Date"
                        value={{ startDate: dateRange.start, endDate: dateRange.end }}
                        onChange={dates => {
                            setDateRange({ start: dates.startDate || '', end: dates.endDate || '' });
                        }}
                    />
                    <div className="flex items-center gap-2 shrink-0">
                        <IconButton
                            onClick={handleButtonClick}
                            disabled={!isButtonEnabled}
                            icon={<SearchIcon />}
                        />
                        {showClearFilterButton && (
                            <ClearButton onClick={handleClearFilters} />
                        )}
                    </div>
                </div>
            </div>
            <div className="relative">
               

                {loading ? (
                    <div className="text-center my-4"><LoadingAnimation /></div>
                ) : (
                    <div>
                        <TableComponent
                            headers={['S.No', 'Onset Date', 'Conditions', 'Treated By', 'Status']}
                            data={medicalhistories}
                            loading={loading}
                            error={error}
                            renderRow={renderRow}
                        />
                        
                    </div>
                )}

                <TablePaginationComponent
                    count={totalMedicalhistories}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={(_event, newPage) => {
                        setPage(newPage);
                    }}
                    onRowsPerPageChange={(event) => {
                        const newRowsPerPage = parseInt(event.target.value, 10);
                        setRowsPerPage(newRowsPerPage);
                        setPage(0);
                    }}
                />
                {/* Snackbar Component */}
                <SnackbarMessage
                    open={snackbarOpen}
                    message={snackbarMessage}
                    severity={snackbarSeverity}
                    onClose={() => setSnackbarOpen(false)}
                />
            </div>

        </div>
    );
};
export default MedicalHistoryForm;

